/**
 * Grading scales:
 * - Primary & Junior Secondary (Form 1–2): US letter scale (A, AB, B, C, D, F)
 * - Senior Secondary (Form 3–4): MSCE numeric grades (1–9)
 * - University: GPA letter grades
 */

/** Official MSCE (Malawi School Certificate of Education) grade scale. */
export const MSCE_GRADE_SCALE = [
    { grade: "1", range: "80 – 100", min: 80, max: 100, label: "Distinction" },
    { grade: "2", range: "70 – 79", min: 70, max: 79, label: "Distinction" },
    { grade: "3", range: "65 – 69", min: 65, max: 69, label: "Credit" },
    { grade: "4", range: "60 – 64", min: 60, max: 64, label: "Credit" },
    { grade: "5", range: "55 – 59", min: 55, max: 59, label: "Credit" },
    { grade: "6", range: "50 – 54", min: 50, max: 54, label: "Credit" },
    { grade: "7", range: "45 – 49", min: 45, max: 49, label: "Pass" },
    { grade: "8", range: "40 – 44", min: 40, max: 44, label: "Pass" },
    { grade: "9", range: "0 – 39", min: 0, max: 39, label: "Fail" },
];

/** @typedef {{ grade: string, label: string, passed: boolean, color?: string, usGrade?: string, scale?: string }} GradeResult */

export function normalizeLevelCode(levelCode, sclassName) {
    const code = String(levelCode || "").trim().replace(/\s+/g, "");
    if (code) return code;
    const name = String(sclassName || "");
    const form = name.match(/Form\s*([1-4])/i);
    if (form) return `Form${form[1]}`;
    const std = name.match(/Std\s*([1-8])/i);
    if (std) return `Std${std[1]}`;
    return "";
}

/** Reference rows for UI (Primary & Junior Secondary US scale). */
export const US_PRIMARY_JUNIOR_GRADE_SCALE = [
    { range: "75 – 100", letter: "A", label: "Distinction" },
    { range: "70 – 74", letter: "AB", label: "Marginal Distinction" },
    { range: "60 – 69", letter: "B", label: "Credit" },
    { range: "50 – 59", letter: "C", label: "Pass" },
    { range: "40 – 49", letter: "D", label: "Bare Pass" },
    { range: "35 – 39", letter: "F", label: "Marginal Fail" },
    { range: "0 – 34", letter: "F", label: "Fail" },
];

/** Primary and Junior Secondary (Form 1 & 2) use the US grading scale. */
export function usesUsGradingScale(educationLevel, levelCode, sclassName) {
    const level = String(educationLevel || "").trim();
    const code = normalizeLevelCode(levelCode, sclassName);
    if (level === "Primary") return true;
    if (level === "Secondary" && /^Form[12]$/i.test(code)) return true;
    return false;
}

/** @param {number} marks */
export function getUsPrimaryJuniorGrade(marks) {
    const safeMarks = Math.max(0, Math.min(100, Number(marks) || 0));

    if (safeMarks >= 75) {
        return {
            grade: "A",
            usGrade: "A",
            label: "Distinction",
            passed: true,
            color: "success",
            scale: "US",
        };
    }
    if (safeMarks >= 70) {
        return {
            grade: "AB",
            usGrade: "AB",
            label: "Marginal Distinction",
            passed: true,
            color: "success",
            scale: "US",
        };
    }
    if (safeMarks >= 60) {
        return {
            grade: "B",
            usGrade: "B",
            label: "Credit",
            passed: true,
            color: "info",
            scale: "US",
        };
    }
    if (safeMarks >= 50) {
        return {
            grade: "C",
            usGrade: "C",
            label: "Pass",
            passed: true,
            color: "info",
            scale: "US",
        };
    }
    if (safeMarks >= 40) {
        return {
            grade: "D",
            usGrade: "D",
            label: "Bare Pass",
            passed: true,
            color: "warning",
            scale: "US",
        };
    }
    if (safeMarks >= 35) {
        return {
            grade: "F",
            usGrade: "F",
            label: "Marginal Fail",
            passed: false,
            color: "error",
            scale: "US",
        };
    }
    return {
        grade: "F",
        usGrade: "F",
        label: "Fail",
        passed: false,
        color: "error",
        scale: "US",
    };
}

/** MSCE grades for senior secondary (Form 3–4). */
export function getMsceGrade(marks) {
    const safeMarks = Math.max(0, Math.min(100, Math.round(Number(marks) || 0)));
    const row =
        MSCE_GRADE_SCALE.find((band) => safeMarks >= band.min && safeMarks <= band.max) ||
        MSCE_GRADE_SCALE[MSCE_GRADE_SCALE.length - 1];
    const passed = row.label !== "Fail";
    const color =
        row.label === "Fail" ? "error" : row.label === "Pass" ? "warning" : row.label === "Distinction" ? "success" : "info";
    return { grade: row.grade, label: row.label, passed, color, scale: "MSCE" };
}

/** @deprecated Use getMsceGrade — kept for existing imports. */
export function getManebSecondaryGrade(marks) {
    return getMsceGrade(marks);
}

/**
 * @param {number} marks
 * @param {string} [educationLevel]
 * @param {string} [levelCode]
 * @param {string} [sclassName]
 * @returns {GradeResult}
 */
export function getMalawiGrade(marks, educationLevel, levelCode, sclassName) {
    const level = String(educationLevel || "Secondary").trim();

    if (level === "University") {
        return { grade: "", label: "", passed: false };
    }

    if (usesUsGradingScale(level, levelCode, sclassName)) {
        return getUsPrimaryJuniorGrade(marks);
    }

    if (level === "Primary" || level === "Secondary") {
        return getMsceGrade(marks);
    }

    return { grade: "", label: "", passed: false };
}

/** @param {Record<string, unknown>|null|undefined} classOrStudent */
export function getGradeForClass(marks, classOrStudent) {
    const educationLevel =
        classOrStudent?.educationLevel ||
        classOrStudent?.sclass?.educationLevel ||
        classOrStudent?.sclassName?.educationLevel ||
        "Secondary";
    const levelCode =
        classOrStudent?.levelCode ||
        classOrStudent?.sclass?.levelCode ||
        classOrStudent?.sclassName?.levelCode ||
        "";
    const sclassName =
        classOrStudent?.sclassName?.sclassName ||
        (typeof classOrStudent?.sclassName === "string" ? classOrStudent.sclassName : "") ||
        classOrStudent?.sclass?.sclassName ||
        "";
    return getMalawiGrade(marks, educationLevel, levelCode, sclassName);
}

export function getUniversityGrade(totalScore) {
    const safeScore = Math.max(0, Math.min(100, Number(totalScore) || 0));
    if (safeScore >= 75) return { letter: "A", point: 4.0, label: "Excellent", passed: true, color: "success" };
    if (safeScore >= 65) return { letter: "B", point: 3.0, label: "Good", passed: true, color: "info" };
    if (safeScore >= 55) return { letter: "C", point: 2.0, label: "Satisfactory", passed: true, color: "warning" };
    if (safeScore >= 45) return { letter: "D", point: 1.0, label: "Pass", passed: true, color: "warning" };
    return { letter: "F", point: 0.0, label: "Fail", passed: false, color: "error" };
}

export function computeWeightedTotal(caScore, examScore) {
    const ca = Number(caScore) || 0;
    const exam = Number(examScore) || 0;
    return Number((ca * 0.4 + exam * 0.6).toFixed(2));
}

export function getClassificationColor(classification) {
    const value = String(classification || "");
    if (value === "First Class") return { bg: "#fff8e1", color: "#b26a00", border: "#ffd54f" };
    if (value === "Upper Second Class") return { bg: "#e8f5e9", color: "#2e7d32", border: "#81c784" };
    if (value === "Lower Second Class") return { bg: "#e3f2fd", color: "#1565c0", border: "#64b5f6" };
    if (value === "Pass") return { bg: "#fff3e0", color: "#ef6c00", border: "#ffb74d" };
    return { bg: "#ffebee", color: "#c62828", border: "#ef9a9a" };
}

export function gpaBandLetter(gpa) {
    const value = Number(gpa) || 0;
    if (value >= 3.5) return "A";
    if (value >= 3.0) return "B";
    if (value >= 2.5) return "C";
    if (value >= 2.0) return "D";
    return "F";
}

export function exportCsv(filename, rows) {
    const csv = rows
        .map((row) =>
            row
                .map((value) => {
                    const text = String(value ?? "");
                    if (/[",\n\r]/.test(text)) return `"${text.replace(/"/g, '""')}"`;
                    return text;
                })
                .join(",")
        )
        .join("\n");
    const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url;
    link.setAttribute("download", filename);
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
}
